<script lang="ts">
    import type {Document} from 'src/typings';
    import type {TabGroup, TabId} from 'src/features/erp/components/erp-tab-pages/types';
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {OptimisationSumsResponse} from './types';
    import {KpLoadableContainer} from 'shared/layouts';
    import {LabeledValue} from 'src/features/erp-sutor/shared';
    import {ErpTabPages, TabPagesRouter} from 'src/features/erp/components';
    import {getCurrentLanguage, getInjector} from 'src/core';
    import {onDestroy, onMount} from 'svelte';
    import {createProjectReportOptimisationContext} from './project-report-optimisation-context';
    import {priceFormatter} from 'shared/utils/pipes';
    import {pipe} from 'core/utils';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import {fly} from 'svelte/transition';
    import {SUTOR_REPORTS_OPTIMISATION_PAGE} from 'src/features/erp-sutor/sutor-constants';
    import {RecordGridService} from 'src/features/record-grid/services/record-grid.service';
    import {ProjectReportOptimisationService} from './project-report-optimisation.service';
    import ReportOptimisationEditationTab from './editation/ReportOptimisationEditationTab.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import ReportOptimisationOverviewsTab from './overviews/ReportOptimisationOverviewsTab.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpVerticalSeparator from 'shared/ui-widgets/separator/KpVerticalSeparator.svelte';

    export let record: Document;

    const recordGridService = getInjector().getByClass(RecordGridService);
    const service = getInjector().getByClass(ProjectReportOptimisationService);
    const context = createProjectReportOptimisationContext(record, service);
    const priceFormat = priceFormatter(getCurrentLanguage());

    let loading = true;

    let optimisedBusinessItemRecord: RecordRow | null = null;
    let overviewedBusinessItemRecord: RecordRow | null = null;
    let optimisationSums: OptimisationSumsResponse | null = null;
    let tabToSelect: TabId | null = null;

    const optimisedBusinessItemRecordUnsubscribe = context.optimisedBusinessItemRecord$.subscribe((currentOptimisedBusinessItemRecord) => optimisedBusinessItemRecord = currentOptimisedBusinessItemRecord);
    // When the signal is set, we need to switch to the overview tab
    const openOverviewSignalUnsubscribe = context.openOverviewSignal$.subscribe((businessItemRecord) => {
        overviewedBusinessItemRecord = businessItemRecord;

        if (exists(businessItemRecord)) {
            tabToSelect = 'tab-overviews';
        }
    });

    // Tabs setup
    const tabGroups: TabGroup[] = [];

    onMount(async () => {
        const optimisationFond = await recordGridService.getFond(SUTOR_REPORTS_OPTIMISATION_PAGE.fond);

        // We don't want to wait for this - just let it load in the background
        service.getReportSums(record).then((response) => optimisationSums = response);

        tabGroups.push({
            id: 'tab-editation',
            label: 'Náklady k optimalizaci',
            icon: 'list',
            render: {
                component: ReportOptimisationEditationTab,
                props: {
                    fond: optimisationFond
                }
            }
        });

        tabGroups.push({
            id: 'tab-overviews',
            label: 'Přehledy',
            icon: 'info',
            render: {
                component: ReportOptimisationOverviewsTab
            }
        });

        loading = false;
    });

    onDestroy(() => {
        cleanup(optimisedBusinessItemRecordUnsubscribe, openOverviewSignalUnsubscribe);
    });

    const handleUnselectOptimisedBusinessItem = () => {
        context.setOptimisedBusinessItemRecord(null);
    };

    const handleUnselectOverviewedBusinessItem = () => {
        context.setOpenOverviewSignal(null);
        tabToSelect = 'tab-editation';
    };

    const handleTabChange = (event: CustomEvent<TabId>) => {
        if (event.detail === 'tab-editation') {
            context.setOptimisedBusinessItemRecord(null);
            context.setOpenOverviewSignal(null);
        }
    };
</script>

<KpLoadableContainer fillAvailableSpace {loading}>
    <ErpTabPages {tabGroups}
                 {tabToSelect}
                 on:tab-change={handleTabChange}
                 parentIdentifier="project-report-optimisation"
                 pageClass="reports-optimisation-tab-pages"
                 withoutUrlManagement>

        <svelte:fragment slot="additional-content" let:activeTab>
            {#if activeTab === 'tab-overviews' && exists(overviewedBusinessItemRecord)}
                <KpVerticalSeparator height="16px"/>

                <div class="additional-content-container" in:fly={{x: 10, duration: 250}}>
                    <KpButton buttonStyle="brand-orange" on:click={handleUnselectOverviewedBusinessItem}>
                        <IconedContent icon="arrow-small-left">Zpět na tabulku</IconedContent>
                    </KpButton>
                </div>
            {/if}

            {#if activeTab === 'tab-editation'}
                <KpVerticalSeparator height="16px"/>

                {#if exists(optimisedBusinessItemRecord)}
                    <div class="additional-content-container" in:fly={{x: 10, duration: 250}}>
                        <KpButton buttonStyle="brand-orange" on:click={handleUnselectOptimisedBusinessItem}>
                            <IconedContent icon="cross-circle">Zavřít optimalizaci</IconedContent>
                        </KpButton>

                        <LabeledValue label="Optimalizovaná položka">{optimisedBusinessItemRecord.name}</LabeledValue>
                    </div>
                {:else}
                    <div class="additional-content-container" in:fly={{x: 10, duration: 250}}>
                        <small class="text-muted">Celkové součty:</small>

                        {#if !exists(optimisationSums)}
                            <KpLoadingBlock size="xs"/>
                        {:else}
                            <LabeledValue label="Vykázáno">
                                {pipe(optimisationSums.reportedPrice, priceFormat)}
                            </LabeledValue>

                            <LabeledValue label="Účtováno">
                                {pipe(optimisationSums.chargedPrice, priceFormat)}
                            </LabeledValue>
                        {/if}
                    </div>
                {/if}
            {/if}
        </svelte:fragment>

        <TabPagesRouter/>
    </ErpTabPages>
</KpLoadableContainer>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .additional-content-container {
        display: flex;
        align-items: center;
        gap: @spacing-l;

        small {
            white-space: nowrap;
        }
    }
</style>